/**
 * EcosystemJoinBlock: `ecosystem-join` content block.
 *
 * data: { heading, body, buttons: [{ text, link, variant }] }
 *
 * Full-width dark navy CTA section with a heading, body paragraph, and 1–4
 * action buttons. The dark background is applied via the `.ecosystem-join-block`
 * CSS class (defined in Content.css); no inline styles.
 *
 * Each button uses the same variant allowlist and link-routing logic as
 * CtaButtonBlock: relative paths go through React Router's <Link>; external
 * and anchor URLs (mailto:, tel:) render as plain <a>. Buttons with disallowed
 * link URLs (javascript:, data:, etc.) are silently skipped.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button, Container, Row, Col } from 'react-bootstrap';

import {
    isAllowedLinkUrl,
    isRawAnchorUrl,
    isExternalHttpUrl,
} from '../utils/urlValidator';

// Must stay in sync with CTA_BUTTON_VARIANTS (editor/blockTypes.js) and
// ALLOWED_VARIANTS in CtaButtonBlock.jsx. See CtaButtonBlock for the circular-
// dependency reason a shared import is not used.
const ALLOWED_VARIANTS = [
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'info',
    'light',
    'dark',
    'accent',
    'link',
    'outline-primary',
    'outline-secondary',
    'outline-success',
    'outline-danger',
    'outline-warning',
    'outline-info',
    'outline-light',
    'outline-dark',
];

function renderButton(btn, index) {
    const { text, link, variant } = btn || {};
    if (!text || !link) return null;
    if (!isAllowedLinkUrl(link)) return null;

    const safeVariant = ALLOWED_VARIANTS.includes(variant)
        ? variant
        : 'primary';
    const btnProps = {
        key: index,
        variant: safeVariant,
        size: 'lg',
        className: 'rounded-pill px-4',
    };

    return isRawAnchorUrl(link) ? (
        <Button
            {...btnProps}
            href={link}
            {...(isExternalHttpUrl(link) && {
                target: '_blank',
                rel: 'noopener noreferrer',
            })}
        >
            {text}
        </Button>
    ) : (
        <Button {...btnProps} as={Link} to={link}>
            {text}
        </Button>
    );
}

export default function EcosystemJoinBlock({ data }) {
    const { heading = '', body = '', buttons = [] } = data || {};

    return (
        <div className="ecosystem-join-block py-5">
            <Container>
                <Row className="justify-content-center text-center">
                    <Col lg={8}>
                        {heading && (
                            <h2 className="fw-bold mb-3 text-white">
                                {heading}
                            </h2>
                        )}
                        {body && (
                            <p className="lead mb-4 text-white opacity-75">
                                {body}
                            </p>
                        )}
                        {buttons.length > 0 && (
                            <div className="d-grid d-md-flex gap-2 gap-md-3 justify-content-md-center">
                                {buttons.map((btn, i) => renderButton(btn, i))}
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

EcosystemJoinBlock.propTypes = {
    data: PropTypes.shape({
        heading: PropTypes.string,
        body: PropTypes.string,
        buttons: PropTypes.arrayOf(
            PropTypes.shape({
                text: PropTypes.string,
                link: PropTypes.string,
                variant: PropTypes.string,
            })
        ),
    }),
};

EcosystemJoinBlock.defaultProps = {
    data: {},
};
